import { useEffect, useState, type ImgHTMLAttributes } from "react";

const TRANSPARENT_PIXEL = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

const loadedUrls = new Set<string>();

export interface SafeImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "placeholder"> {
  src?: string;
  alt?: string;
  fit?: "contain" | "cover";
  /** 加载完成回调，ok=false 表示断图（加载失败或无 url） */
  onResolved?: (ok: boolean) => void;
}

/**
 * 安全图片：预加载 url，加载失败或无 url 时返回 null（彻底不渲染，断图不展示）。
 * 区别于 @components/Image：本组件不显示空占位，适用于断图需隐藏的场景。
 */
export const SafeImage = ({ src, alt = "", fit = "cover", style, onResolved, ...rest }: SafeImageProps) => {
  const [bg, setBg] = useState<string>(() => (src && loadedUrls.has(src) ? src : ""));
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    if (!src) {
      setBg("");
      setError(true);
      onResolved?.(false);
      return;
    }
    if (loadedUrls.has(src)) {
      setBg(src);
      setError(false);
      onResolved?.(true);
      return;
    }
    let cancelled = false;
    setBg("");
    setError(false);
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      loadedUrls.add(src);
      setBg(src);
      setError(false);
      onResolved?.(true);
    };
    img.onerror = () => {
      if (cancelled) return;
      setError(true);
      onResolved?.(false);
    };
    img.src = src;
    return () => {
      cancelled = true;
    };
    // onResolved 不纳入依赖，避免父组件每次渲染产生新引用触发重复加载
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  if (!src || error) return null;

  return (
    <img
      {...rest}
      src={TRANSPARENT_PIXEL}
      alt={alt}
      style={{
        backgroundImage: bg ? `url("${bg}")` : undefined,
        backgroundSize: fit,
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        ...style,
      }}
    />
  );
};
